import Phaser from 'phaser';
import { ts } from '../utils/textStyle';
import { GAME_HEIGHT, GAME_WIDTH } from '../config/game';
import { BossToken } from '../entities/boss';
import { getTunables } from '../config/tunables';
import { BossType, getBossDefinition } from './bossCatalog';
import type { PartnerType } from './shopCatalog';
import type { DamageSource } from './runState';

export type BossSpawnPoint = {
  x: number;
  y: number;
  side: 'top' | 'right' | 'bottom' | 'left';
};

export interface BossSystemCallbacks {
  getBossFromPool: () => BossToken;
  activateSummonedMinion: (spawnPoint: BossSpawnPoint, timeMs: number) => void;
  getActiveBossMinionCount: () => number;
}

export interface BossThreatSnapshot {
  x: number;
  y: number;
  radius: number;
  width?: number;
  height?: number;
  source: DamageSource;
}

export class BossSystem {
  private readonly shockwaves: Phaser.GameObjects.Group;
  private readonly hazards: Phaser.GameObjects.Group;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly player: Phaser.GameObjects.Arc,
    private readonly callbacks: BossSystemCallbacks,
  ) {
    this.shockwaves = this.scene.add.group({
      maxSize: 8,
      runChildUpdate: false,
    });
    this.hazards = this.scene.add.group({
      maxSize: 48,
      runChildUpdate: false,
    });
    this.scene.physics.add.overlap(this.player, this.shockwaves, (_player, shockwaveObj) => {
      const shockwave = shockwaveObj as Phaser.GameObjects.Rectangle;
      if (!shockwave.active) {
        return;
      }
      this.deactivateShockwave(shockwave);
      this.scene.events.emit('player-damage-request', {
        amount: getTunables().boss.shockwaveDamage,
        source: 'shockwave',
      });
    });
    this.scene.physics.add.overlap(this.player, this.hazards, (_player, hazardObj) => {
      const hazard = hazardObj as Phaser.GameObjects.Shape;
      if (!hazard.active) {
        return;
      }
      const now = this.scene.time.now;
      const nextHitAt = (hazard.getData('nextHitAt') as number | undefined) ?? 0;
      if (now < nextHitAt) {
        return;
      }
      hazard.setData('nextHitAt', now + ((hazard.getData('hitCooldownMs') as number | undefined) ?? 700));
      this.scene.events.emit('player-damage-request', {
        amount: (hazard.getData('damage') as number | undefined) ?? 8,
        source: (hazard.getData('damageSource') as DamageSource | undefined) ?? 'boss-hazard',
      });
    });
  }

  spawnBoss(type: BossType, x: number, y: number, timeMs: number) {
    const definition = getBossDefinition(type);
    const boss = this.callbacks.getBossFromPool();
    boss.activateBoss(definition, x, y, timeMs);
    this.showBossIntro(definition.label, definition.skillSummary);
    return boss;
  }

  updateBoss(boss: BossToken, timeMs: number) {
    boss.updateBossVisual(timeMs);
    const definition = boss.requireDefinition();

    if (definition.behavior === 'rift-core') this.updateRiftCore(boss, timeMs);
    if (definition.behavior === 'glutton-slime') this.updateGluttonSlime(boss);
    if (definition.behavior === 'crystal-titan') this.updateCrystalTitan(boss, timeMs);
    if (definition.behavior === 'hive-heart') this.updateHiveHeart(boss, timeMs);
    if (definition.behavior === 'pendulum-knight') this.updatePendulumKnight(boss, timeMs);
    if (definition.behavior === 'shadow-weaver') this.updateShadowWeaver(boss, timeMs);
    if (definition.behavior === 'iron-bastion') this.updateIronBastion(boss, timeMs);
    if (definition.behavior === 'storm-eye') this.updateStormEye(boss, timeMs);
    if (definition.behavior === 'blood-furnace') this.updateBloodFurnace(boss, timeMs);
    if (definition.behavior === 'mirror-shade') this.updateMirrorShade(boss, timeMs);
    if (
      definition.behavior === 'training-dummy'
    ) {
      this.updateChasingBoss(boss, timeMs);
    }
  }

  updateEffects(timeMs: number) {
    for (const child of this.shockwaves.getChildren()) {
      const shockwave = child as Phaser.GameObjects.Rectangle;
      if (shockwave.active && timeMs >= (shockwave.getData('expiresAt') as number)) {
        this.deactivateShockwave(shockwave);
      }
    }
    for (const child of this.hazards.getChildren()) {
      const hazard = child as Phaser.GameObjects.Shape;
      if (hazard.active && timeMs >= ((hazard.getData('expiresAt') as number | undefined) ?? Infinity)) {
        this.deactivateHazard(hazard);
      }
    }
  }

  stopAll() {
    this.clearEffects();
  }

  clearEffects() {
    for (const child of this.shockwaves.getChildren()) {
      const shockwave = child as Phaser.GameObjects.Rectangle;
      if (shockwave.active) {
        this.deactivateShockwave(shockwave);
      }
    }
    for (const child of this.hazards.getChildren()) {
      const hazard = child as Phaser.GameObjects.Shape;
      if (hazard.active) {
        this.deactivateHazard(hazard);
      }
    }
  }

  getActiveThreats(): BossThreatSnapshot[] {
    const threats: BossThreatSnapshot[] = [];
    for (const child of this.shockwaves.getChildren()) {
      const shockwave = child as Phaser.GameObjects.Rectangle;
      if (!shockwave.active) {
        continue;
      }
      threats.push({
        x: shockwave.x,
        y: shockwave.y,
        radius: Math.max(shockwave.displayWidth, shockwave.displayHeight) / 2 + 18,
        width: shockwave.displayWidth,
        height: shockwave.displayHeight,
        source: 'shockwave',
      });
    }
    for (const child of this.hazards.getChildren()) {
      const hazard = child as Phaser.GameObjects.Shape;
      if (!hazard.active) {
        continue;
      }
      const source = (hazard.getData('damageSource') as DamageSource | undefined) ?? 'boss-hazard';
      const radius = hazard instanceof Phaser.GameObjects.Arc
        ? hazard.radius * Math.max(hazard.scaleX, hazard.scaleY) + 24
        : Math.max(hazard.displayWidth, hazard.displayHeight) / 2 + 24;
      threats.push({
        x: hazard.x,
        y: hazard.y,
        radius,
        width: hazard instanceof Phaser.GameObjects.Rectangle ? hazard.displayWidth : undefined,
        height: hazard instanceof Phaser.GameObjects.Rectangle ? hazard.displayHeight : undefined,
        source,
      });
    }
    return threats;
  }

  resolveIncomingDamage(boss: BossToken, amount: number, sourceType?: PartnerType | 'relic', roll = 0) {
    const definition = boss.requireDefinition();
    if (definition.behavior === 'hive-heart') {
      return this.resolveHiveHeartDamage(boss, amount);
    }
    if (definition.behavior === 'pendulum-knight') {
      return this.resolvePendulumKnightDamage(boss, amount, sourceType);
    }
    if (definition.behavior === 'iron-bastion') {
      return this.resolveIronBastionDamage(boss, amount);
    }
    if (definition.behavior === 'mirror-shade') {
      return this.resolveMirrorShadeDamage(boss, amount, roll);
    }
    if (definition.behavior === 'blood-furnace') {
      const vulnerableUntil = (boss.getData('bossVulnerableUntil') as number | undefined) ?? 0;
      return this.scene.time.now < vulnerableUntil ? amount * 1.5 : amount;
    }
    return amount;
  }

  private updateRiftCore(boss: BossToken, timeMs: number) {
    if (boss.shockwaveChargingUntil > 0) {
      boss.body.setVelocity(0, 0);
      if (timeMs >= boss.shockwaveChargingUntil) {
        this.fireRiftCoreShockwave(boss, timeMs);
      }
      return;
    }

    this.chasePlayer(boss);

    if (timeMs >= boss.nextShockwaveAt) {
      this.prepareRiftCoreShockwave(boss, timeMs);
      return;
    }

    if (boss.getBossPhase() === 2 && timeMs >= boss.nextSummonAt) {
      this.summonRiftCoreMinions(boss, timeMs);
    }
  }

  private updateChasingBoss(boss: BossToken, _timeMs: number) {
    this.chasePlayer(boss);
  }

  private updateGluttonSlime(boss: BossToken) {
    const request: {
      x: number;
      y: number;
      seekRange: number;
      consumeRange: number;
      target?: { x: number; y: number };
      consumed?: boolean;
    } = {
      x: boss.x,
      y: boss.y,
      seekRange: boss.getBossPhase() === 2 ? 260 : 210,
      consumeRange: boss.radius + 16,
    };
    this.scene.events.emit('boss-material-eat-request', request);

    let ring = boss.getData('eatWarningRing') as Phaser.GameObjects.Arc;
    if (!ring) {
        ring = this.scene.add.circle(boss.x, boss.y, request.seekRange * 0.8);
        ring.setStrokeStyle(2, 0xff5555, 0.6);
        ring.setDepth(15);
        
        // Add a gentle pulse to the stroke alpha
        this.scene.tweens.add({
            targets: ring,
            alpha: 0.3,
            duration: 600,
            yoyo: true,
            repeat: -1
        });
        
        boss.setData('eatWarningRing', ring);
        
        // Cleanup ring when boss dies
        boss.once(Phaser.Scenes.Events.DESTROY, () => {
            if (ring) ring.destroy();
        });
    } else {
        ring.setPosition(boss.x, boss.y);
        ring.setRadius(request.seekRange * 0.8);
        if (!boss.active) {
            ring.destroy();
            boss.setData('eatWarningRing', null);
        }
    }

    if (request.consumed) {
      boss.hp = Math.min(boss.maxHp, boss.hp + 12);
      const eaten = ((boss.getData('bossEatenMaterials') as number | undefined) ?? 0) + 1;
      boss.setData('bossEatenMaterials', eaten);
      const sizeBonus = Math.min(0.42, eaten * 0.035);
      boss.setData('bossSizeBonus', sizeBonus);
      const collisionRadius = boss.radius * (1 + sizeBonus);
      boss.body.setCircle(collisionRadius);
      boss.body.setOffset(-collisionRadius, -collisionRadius);
      this.showPulse(boss.x, boss.y, 42 + Math.min(24, eaten * 2), 0x9dff7a, 220);
      boss.body.setVelocity(0, 0);
      return;
    }

    if (request.target) {
      this.chasePoint(boss, request.target.x, request.target.y);
      return;
    }

    this.chasePlayer(boss);
  }

  private updateCrystalTitan(boss: BossToken, timeMs: number) {
    this.chasePlayer(boss);
    const nextActionAt = (boss.getData('bossNextActionAt') as number | undefined) ?? 0;
    if (timeMs < nextActionAt) {
      return;
    }

    const phase = boss.getBossPhase();
    boss.setData('bossNextActionAt', timeMs + (phase === 2 ? 2500 : 3200));
    this.spawnCrystalSpikePattern(boss, timeMs, phase === 2);
  }

  private updateHiveHeart(boss: BossToken, timeMs: number) {
    boss.body.setVelocity(0, 0);
    const phase = boss.getBossPhase();
    const brokenUntil = (boss.getData('bossShieldBrokenUntil') as number | undefined) ?? 0;
    const shieldHp = (boss.getData('bossShieldHp') as number | undefined) ?? 0;
    if (shieldHp <= 0 && timeMs >= brokenUntil) {
      boss.setData('bossShieldHp', phase === 2 ? 60 : 40);
      this.showPulse(boss.x, boss.y, boss.radius + 22, 0xa8ff8f, 280);
    }

    const nextSummonAt = (boss.getData('bossNextSummonAt') as number | undefined) ?? 0;
    if (timeMs >= nextSummonAt) {
      boss.setData('bossNextSummonAt', timeMs + (phase === 2 ? 1500 : 2000));
      this.summonHiveMinion(boss, timeMs);
    }

    const activeShield = ((boss.getData('bossShieldHp') as number | undefined) ?? 0) > 0;
    boss.setStrokeStyle(activeShield ? 6 : 3, activeShield ? 0xd8ffd9 : boss.requireDefinition().stroke, activeShield ? 0.96 : 0.74);
  }

  private updatePendulumKnight(boss: BossToken, timeMs: number) {
    this.chasePlayer(boss);
    const nextActionAt = (boss.getData('bossNextActionAt') as number | undefined) ?? 0;
    if (timeMs < nextActionAt) {
      return;
    }

    const previous = (boss.getData('bossImmunityMode') as string | undefined) ?? 'ranged';
    const next = previous === 'melee' ? 'ranged' : 'melee';
    boss.setData('bossImmunityMode', next);
    boss.setData('bossNextActionAt', timeMs + (boss.getBossPhase() === 2 ? 2400 : 3600));
    boss.setStrokeStyle(5, next === 'melee' ? 0xffd35a : 0x8ad8ff, 0.96);
    this.showModeText(boss.x, boss.y - 38, next === 'melee' ? '近战免疫' : '远程免疫', next === 'melee' ? '#ffd35a' : '#8ad8ff');
    if (boss.getBossPhase() === 2) {
      this.queuePendulumDash(boss);
    }
  }

  private updateShadowWeaver(boss: BossToken, timeMs: number) {
    this.chasePlayer(boss);
    const nextActionAt = (boss.getData('bossNextActionAt') as number | undefined) ?? 0;
    if (timeMs < nextActionAt) {
      return;
    }

    const phase = boss.getBossPhase();
    boss.setData('bossNextActionAt', timeMs + (phase === 2 ? 2000 : 3000));
    const oldX = boss.x;
    const oldY = boss.y;
    const angle = timeMs * 0.0017;
    const distance = phase === 2 ? 112 : 92;
    const x = Phaser.Math.Clamp(this.player.x + Math.cos(angle) * distance, 36, GAME_WIDTH - 36);
    const y = Phaser.Math.Clamp(this.player.y + Math.sin(angle) * distance, 116, GAME_HEIGHT - 36);
    boss.setPosition(x, y);
    boss.body.setVelocity(0, 0);
    this.activateShadowPool(oldX, oldY, timeMs, phase);
    this.showPulse(x, y, 40, 0xbd6cff, 220);
    if (phase === 2) {
      this.fireSimpleProjectile(x, y, this.player.x, this.player.y, 0x9d63ff, 7, 210, 9, timeMs + 1800);
    }
  }

  private updateIronBastion(boss: BossToken, timeMs: number) {
    const stunnedUntil = (boss.getData('bossStunnedUntil') as number | undefined) ?? 0;
    if (timeMs < stunnedUntil) {
      boss.body.setVelocity(0, 0);
      return;
    }

    const chargingUntil = (boss.getData('bossChargeUntil') as number | undefined) ?? 0;
    if (chargingUntil > 0) {
      if (timeMs >= chargingUntil) {
        boss.setData('bossChargeUntil', 0);
        boss.body.setVelocity(0, 0);
        if (boss.getBossPhase() === 1) {
          boss.setData('bossStunnedUntil', timeMs + 1000);
          this.showModeText(boss.x, boss.y - 42, '眩晕', '#d8edf7');
        }
      }
      return;
    }

    const angle = Phaser.Math.Angle.Between(boss.x, boss.y, this.player.x, this.player.y);
    boss.setData('bossFacingAngle', angle);
    this.chasePlayer(boss);
    this.showIronShield(boss, angle);

    const nextActionAt = (boss.getData('bossNextActionAt') as number | undefined) ?? 0;
    if (timeMs < nextActionAt) {
      return;
    }

    boss.setData('bossNextActionAt', timeMs + (boss.getBossPhase() === 2 ? 2800 : 3400));
    boss.body.setVelocity(0, 0);
    this.showBossAimLine(boss);
    this.scene.time.delayedCall(800, () => {
      if (!boss.active) {
        return;
      }
      const chargeAngle = Number(boss.getData('bossFacingAngle')) || angle;
      const speed = boss.getBossPhase() === 2 ? 440 : 360;
      boss.setData('bossChargeUntil', this.scene.time.now + 520);
      boss.body.setVelocity(Math.cos(chargeAngle) * speed, Math.sin(chargeAngle) * speed);
    });
  }

  private updateStormEye(boss: BossToken, timeMs: number) {
    const phase = boss.getBossPhase();
    const orbitRadius = 168;
    const angle = timeMs * (phase === 2 ? 0.0009 : 0.0007);
    boss.setPosition(
      GAME_WIDTH / 2 + Math.cos(angle) * orbitRadius,
      GAME_HEIGHT / 2 + Math.sin(angle) * Math.min(orbitRadius, 260),
    );
    boss.body.setVelocity(0, 0);

    const nextActionAt = (boss.getData('bossNextActionAt') as number | undefined) ?? 0;
    if (timeMs >= nextActionAt) {
      boss.setData('bossNextActionAt', timeMs + (phase === 2 ? 1300 : 1600));
      this.fireStormVolley(boss, timeMs, phase === 2 ? 8 : 6);
      if (phase === 2) {
        this.fireStormVolley(boss, -timeMs, 6);
      }
    }

    const nextLightningAt = (boss.getData('bossNextSummonAt') as number | undefined) ?? 0;
    if (phase === 2 && timeMs >= nextLightningAt) {
      boss.setData('bossNextSummonAt', timeMs + 3000);
      this.queueLightningStrike(this.player.x, this.player.y, timeMs);
    }
  }

  private updateBloodFurnace(boss: BossToken, timeMs: number) {
    const lostRatio = 1 - boss.hp / boss.maxHp;
    const definition = boss.requireDefinition();
    const baseSpeed = boss.getBossPhase() === 2 ? definition.phaseTwoSpeed : definition.phaseOneSpeed;
    boss.speed = baseSpeed + Math.floor(lostRatio * 10) * 12;
    this.chasePlayer(boss);

    const nextActionAt = (boss.getData('bossNextActionAt') as number | undefined) ?? 0;
    if (timeMs >= nextActionAt) {
      boss.setData('bossNextActionAt', timeMs + (boss.getBossPhase() === 2 ? 3000 : 5000));
      this.queueFurnaceBlast(boss, timeMs);
    }

    if (boss.getBossPhase() === 2 && timeMs % 380 < 18) {
      this.activateFireTrail(boss.x, boss.y, timeMs);
    }
  }

  private updateMirrorShade(boss: BossToken, timeMs: number) {
    this.chasePlayer(boss);
    this.updateMirrorDecoys(boss, timeMs);
    const nextActionAt = (boss.getData('bossNextActionAt') as number | undefined) ?? 0;
    if (timeMs < nextActionAt) {
      return;
    }

    boss.setData('bossNextActionAt', timeMs + (boss.getBossPhase() === 2 ? 5200 : 7600));
    this.rebuildMirrorDecoys(boss, boss.getBossPhase() === 2 ? 4 : 2);
  }

  private chasePlayer(boss: BossToken) {
    this.chasePoint(boss, this.player.x, this.player.y);
  }

  private chasePoint(boss: BossToken, x: number, y: number) {
    const toPlayer = new Phaser.Math.Vector2(x - boss.x, y - boss.y);
    if (toPlayer.lengthSq() <= 1) {
      boss.body.setVelocity(0, 0);
      return;
    }

    toPlayer.normalize();
    boss.body.setVelocity(toPlayer.x * boss.speed, toPlayer.y * boss.speed);
  }

  private resolveHiveHeartDamage(boss: BossToken, amount: number) {
    const shieldHp = (boss.getData('bossShieldHp') as number | undefined) ?? 0;
    if (shieldHp <= 0) {
      return amount;
    }

    const remainingShield = shieldHp - amount;
    boss.setData('bossShieldHp', Math.max(0, remainingShield));
    this.showShieldHit(boss);
    if (remainingShield > 0) {
      return 0;
    }

    boss.setData('bossShieldBrokenUntil', this.scene.time.now + (boss.getBossPhase() === 2 ? 3000 : 4000));
    this.showModeText(boss.x, boss.y - 42, '护盾破裂', '#d8ffd9');
    return Math.abs(remainingShield);
  }

  private resolvePendulumKnightDamage(boss: BossToken, amount: number, sourceType?: PartnerType | 'relic') {
    if (!sourceType || sourceType === 'relic') {
      return amount;
    }
    const mode = (boss.getData('bossImmunityMode') as string | undefined) ?? 'melee';
    const melee = sourceType === 'sword' || sourceType === 'assassin' || sourceType === 'tank';
    const ranged = sourceType === 'gunner' || sourceType === 'engineer' || sourceType === 'mage' || sourceType === 'summoner';
    if ((mode === 'melee' && melee) || (mode === 'ranged' && ranged)) {
      this.showModeText(boss.x, boss.y - 44, '免疫', mode === 'melee' ? '#ffd35a' : '#8ad8ff');
      return 0;
    }
    return amount;
  }

  private resolveIronBastionDamage(boss: BossToken, amount: number) {
    const facing = (boss.getData('bossFacingAngle') as number | undefined) ?? Phaser.Math.Angle.Between(boss.x, boss.y, this.player.x, this.player.y);
    const incoming = Phaser.Math.Angle.Between(boss.x, boss.y, this.player.x, this.player.y);
    const shieldAngle = boss.getBossPhase() === 2 ? (Math.PI * 2) / 3 : Math.PI / 3;
    if (Math.abs(Phaser.Math.Angle.Wrap(incoming - facing)) <= shieldAngle) {
      this.showModeText(boss.x, boss.y - 44, '盾挡', '#d8edf7');
      return 0;
    }
    return amount;
  }

  private resolveMirrorShadeDamage(boss: BossToken, amount: number, roll: number) {
    const realHitChance = boss.getBossPhase() === 2 ? 0.7 : 0.42;
    if (roll <= realHitChance) {
      this.showModeText(boss.x, boss.y - 42, '真身', '#dcfbff');
      return amount;
    }
    this.showModeText(boss.x, boss.y - 42, '幻影', '#8cf2ff');
    return 0;
  }

  private prepareRiftCoreShockwave(boss: BossToken, timeMs: number) {
    boss.shockwaveChargingUntil = timeMs + 500;
    boss.shockwaveTargetX = this.player.x;
    boss.shockwaveTargetY = this.player.y;
    boss.body.setVelocity(0, 0);
    this.scene.tweens.add({
      targets: boss,
      alpha: 0.35,
      duration: 100,
      yoyo: true,
      repeat: 4,
      onComplete: () => boss.setAlpha(1),
    });
    this.showBossAimLine(boss);
  }

  private fireRiftCoreShockwave(boss: BossToken, timeMs: number) {
    boss.shockwaveChargingUntil = 0;
    const phase = boss.getBossPhase();
    boss.nextShockwaveAt = timeMs + (phase === 2 ? 2000 : 4000);

    const angle = Phaser.Math.Angle.Between(boss.x, boss.y, boss.shockwaveTargetX, boss.shockwaveTargetY);
    const distance = 220;
    const centerX = boss.x + Math.cos(angle) * (distance / 2);
    const centerY = boss.y + Math.sin(angle) * (distance / 2);
    const shockwave = this.getShockwave();
    if (!shockwave) {
      return;
    }

    shockwave.setPosition(centerX, centerY);
    shockwave.setSize(distance, 40);
    shockwave.setDisplaySize(distance, 40);
    shockwave.setRotation(angle);
    shockwave.setFillStyle(0xff3f35, 0.68);
    shockwave.setStrokeStyle(2, 0xffd0b8, 0.92);
    shockwave.setActive(true).setVisible(true);
    shockwave.setDepth(28);
    shockwave.setData('expiresAt', timeMs + 280);

    const body = shockwave.body as Phaser.Physics.Arcade.Body;
    body.enable = true;
    body.setAllowGravity(false);
    body.setSize(distance, 40, true);

    this.scene.tweens.add({
      targets: shockwave,
      alpha: 0.15,
      duration: 280,
      onComplete: () => this.deactivateShockwave(shockwave),
    });
  }

  private summonRiftCoreMinions(boss: BossToken, timeMs: number) {
    boss.nextSummonAt = timeMs + 8000;
    this.showSummonWarning(boss.x, boss.y);

    for (let index = 0; index < 3; index += 1) {
      const angle = (Math.PI * 2 * index) / 3 + timeMs * 0.001;
      this.callbacks.activateSummonedMinion({
        x: Phaser.Math.Clamp(boss.x + Math.cos(angle) * 56, 24, GAME_WIDTH - 24),
        y: Phaser.Math.Clamp(boss.y + Math.sin(angle) * 56, 104, GAME_HEIGHT - 32),
        side: 'top',
      }, timeMs);
    }
  }

  private summonHiveMinion(boss: BossToken, timeMs: number) {
    const cap = boss.getBossPhase() === 2 ? 12 : 8;
    if (this.callbacks.getActiveBossMinionCount() >= cap) {
      return;
    }
    const index = ((boss.getData('bossStateIndex') as number | undefined) ?? 0) + 1;
    boss.setData('bossStateIndex', index);
    const angle = index * 2.399;
    this.callbacks.activateSummonedMinion({
      x: Phaser.Math.Clamp(boss.x + Math.cos(angle) * 62, 24, GAME_WIDTH - 24),
      y: Phaser.Math.Clamp(boss.y + Math.sin(angle) * 62, 104, GAME_HEIGHT - 32),
      side: 'top',
    }, timeMs);
    this.showSummonWarning(boss.x, boss.y);
  }

  private queuePendulumDash(boss: BossToken) {
    const angle = Phaser.Math.Angle.Between(boss.x, boss.y, this.player.x, this.player.y);
    boss.setData('bossFacingAngle', angle);
    this.showBossAimLine(boss);
    this.scene.time.delayedCall(400, () => {
      if (!boss.active || boss.requireDefinition().behavior !== 'pendulum-knight') {
        return;
      }
      const dashAngle = Number(boss.getData('bossFacingAngle')) || angle;
      boss.setData('bossChargeUntil', this.scene.time.now + 360);
      boss.body.setVelocity(Math.cos(dashAngle) * 380, Math.sin(dashAngle) * 380);
    });
  }

  private spawnCrystalSpikePattern(boss: BossToken, timeMs: number, cross: boolean) {
    const index = ((boss.getData('bossStateIndex') as number | undefined) ?? 0) + 1;
    boss.setData('bossStateIndex', index);
    const vertical = index % 2 === 0;
    const x = 42 + ((index * 73) % (GAME_WIDTH - 84));
    const y = 124 + ((index * 97) % (GAME_HEIGHT - 180));
    this.queueCrystalSpike(vertical, vertical ? x : GAME_WIDTH / 2, vertical ? GAME_HEIGHT / 2 : y, timeMs);
    if (cross) {
      this.queueCrystalSpike(!vertical, vertical ? GAME_WIDTH / 2 : x, vertical ? y : GAME_HEIGHT / 2, timeMs);
    }
  }

  private queueCrystalSpike(vertical: boolean, x: number, y: number, timeMs: number) {
    const warning = this.scene.add.rectangle(
      x,
      y,
      vertical ? 18 : GAME_WIDTH - 32,
      vertical ? GAME_HEIGHT - 160 : 18,
      0xffe37a,
      0.24,
    );
    warning.setStrokeStyle(2, 0xfff1b0, 0.72);
    warning.setDepth(26);
    this.scene.tweens.add({
      targets: warning,
      alpha: 0.55,
      duration: 120,
      yoyo: true,
      repeat: 3,
      onComplete: () => warning.destroy(),
    });
    this.scene.time.delayedCall(600, () => {
      this.activateCrystalSpike(vertical, x, y, timeMs + 600);
    });
  }

  private activateCrystalSpike(vertical: boolean, x: number, y: number, timeMs: number) {
    const spike = this.getHazardRectangle();
    if (!spike) {
      return;
    }
    spike.setPosition(x, y);
    spike.setSize(vertical ? 18 : GAME_WIDTH - 32, vertical ? GAME_HEIGHT - 160 : 18);
    spike.setDisplaySize(vertical ? 18 : GAME_WIDTH - 32, vertical ? GAME_HEIGHT - 160 : 18);
    spike.setFillStyle(0x7adfff, 0.46);
    spike.setStrokeStyle(2, 0xd8fbff, 0.88);
    spike.setActive(true).setVisible(true).setAlpha(1);
    spike.setDepth(25);
    spike.setData('expiresAt', timeMs + 18000);
    spike.setData('damage', 8);
    spike.setData('damageSource', 'boss-hazard');
    spike.setData('hitCooldownMs', 720);
    spike.setData('nextHitAt', 0);

    const body = spike.body as Phaser.Physics.Arcade.Body;
    body.enable = true;
    body.setAllowGravity(false);
    body.setSize(vertical ? 18 : GAME_WIDTH - 32, vertical ? GAME_HEIGHT - 160 : 18, true);
  }

  private getHazardRectangle() {
    let hazard = this.hazards.getFirstDead(false) as Phaser.GameObjects.Rectangle | null;
    if (!hazard && this.hazards.getLength() >= 48) {
      hazard = this.hazards.getChildren().find((child) => child.active) as Phaser.GameObjects.Rectangle | undefined ?? null;
      if (hazard) {
        this.deactivateHazard(hazard);
      }
    }
    if (!hazard) {
      hazard = this.scene.add.rectangle(0, 0, 10, 10, 0xffffff, 0.4);
      this.scene.physics.add.existing(hazard);
      this.hazards.add(hazard);
    }
    return hazard;
  }

  private activateShadowPool(x: number, y: number, timeMs: number, phase: number) {
    const pool = this.getHazardCircle();
    if (!pool) {
      return;
    }
    const radius = phase === 2 ? 42 : 42;
    pool.setPosition(x, y);
    pool.setRadius(radius);
    pool.setFillStyle(0x5b347e, 0.44);
    pool.setStrokeStyle(2, 0xbd6cff, 0.72);
    pool.setActive(true).setVisible(true).setAlpha(1);
    pool.setDepth(25);
    pool.setData('expiresAt', timeMs + (phase === 2 ? 6000 : 4200));
    pool.setData('damage', 6);
    pool.setData('damageSource', 'boss-hazard');
    pool.setData('hitCooldownMs', 520);
    pool.setData('nextHitAt', 0);
    
    // Add pulsing alpha effect
    this.scene.tweens.add({
      targets: pool,
      alpha: { from: 0.3, to: 0.8 },
      duration: 700,
      yoyo: true,
      repeat: -1,
    });
    
    const body = pool.body as Phaser.Physics.Arcade.Body;
    body.enable = true;
    body.setAllowGravity(false);
    body.setCircle(radius);
    body.setOffset(-radius, -radius);
    if (phase === 2) {
      this.scene.tweens.add({
        targets: pool,
        scale: 1.5,
        duration: 6000,
        onUpdate: () => {
          const scaledRadius = radius * pool.scale;
          body.setCircle(scaledRadius);
          body.setOffset(-scaledRadius, -scaledRadius);
        },
      });
    } else {
      pool.setScale(1);
    }
  }

  private activateFireTrail(x: number, y: number, timeMs: number) {
    const flame = this.getHazardCircle();
    if (!flame) {
      return;
    }
    flame.setPosition(x, y);
    flame.setRadius(24);
    flame.setFillStyle(0xff6d2e, 0.36);
    flame.setStrokeStyle(2, 0xffd4a6, 0.62);
    flame.setActive(true).setVisible(true).setAlpha(1);
    flame.setDepth(24);
    flame.setData('expiresAt', timeMs + 2000);
    flame.setData('damage', 5);
    flame.setData('damageSource', 'boss-hazard');
    flame.setData('hitCooldownMs', 420);
    flame.setData('nextHitAt', 0);
    const body = flame.body as Phaser.Physics.Arcade.Body;
    body.enable = true;
    body.setAllowGravity(false);
    body.setCircle(24);
    body.setOffset(-24, -24);
  }

  private queueLightningStrike(x: number, y: number, timeMs: number) {
    const warning = this.scene.add.circle(x, y, 38, 0xffe76d, 0.2);
    warning.setStrokeStyle(3, 0xfff6bd, 0.9);
    warning.setDepth(29);
    this.scene.tweens.add({
      targets: warning,
      alpha: 0.6,
      duration: 100,
      yoyo: true,
      repeat: 3,
      onComplete: () => warning.destroy(),
    });
    this.scene.time.delayedCall(420, () => this.activateLightningStrike(x, y, timeMs + 420));
  }

  private activateLightningStrike(x: number, y: number, timeMs: number) {
    const strike = this.getHazardCircle();
    if (!strike) {
      return;
    }
    strike.setPosition(x, y);
    strike.setRadius(34);
    strike.setFillStyle(0xffe76d, 0.52);
    strike.setStrokeStyle(3, 0xfff6bd, 0.94);
    strike.setActive(true).setVisible(true).setAlpha(1);
    strike.setDepth(31);
    strike.setData('expiresAt', timeMs + 260);
    strike.setData('damage', 15);
    strike.setData('damageSource', 'boss-hazard');
    strike.setData('hitCooldownMs', 1000);
    strike.setData('nextHitAt', 0);
    const body = strike.body as Phaser.Physics.Arcade.Body;
    body.enable = true;
    body.setAllowGravity(false);
    body.setCircle(34);
    body.setOffset(-34, -34);
  }

  private queueFurnaceBlast(boss: BossToken, timeMs: number) {
    this.showPulse(boss.x, boss.y, 86, 0xff7d2e, 600);
    this.scene.time.delayedCall(600, () => {
      if (!boss.active) {
        return;
      }
      boss.setData('bossVulnerableUntil', this.scene.time.now + 1500);
      this.showModeText(boss.x, boss.y - 46, '易伤', '#ffd4a6');
      this.activateFurnaceBlast(boss.x, boss.y, timeMs + 600);
    });
  }

  private activateFurnaceBlast(x: number, y: number, timeMs: number) {
    const blast = this.getHazardCircle();
    if (!blast) {
      return;
    }
    blast.setPosition(x, y);
    blast.setRadius(82);
    blast.setFillStyle(0xff4e2e, 0.4);
    blast.setStrokeStyle(4, 0xffd4a6, 0.9);
    blast.setActive(true).setVisible(true).setAlpha(1);
    blast.setDepth(31);
    blast.setData('expiresAt', timeMs + 260);
    blast.setData('damage', 20);
    blast.setData('damageSource', 'boss-hazard');
    blast.setData('hitCooldownMs', 1000);
    blast.setData('nextHitAt', 0);
    const body = blast.body as Phaser.Physics.Arcade.Body;
    body.enable = true;
    body.setAllowGravity(false);
    body.setCircle(82);
    body.setOffset(-82, -82);
  }

  private fireStormVolley(boss: BossToken, timeMs: number, count: number) {
    for (let index = 0; index < count; index += 1) {
      const angle = (Math.PI * 2 * index) / count + timeMs * 0.0012;
      this.fireSimpleProjectile(
        boss.x,
        boss.y,
        boss.x + Math.cos(angle) * 120,
        boss.y + Math.sin(angle) * 120,
        0xffe76d,
        8,
        145,
        10,
        timeMs + 2600,
      );
    }
  }

  private fireSimpleProjectile(
    x: number,
    y: number,
    targetX: number,
    targetY: number,
    color: number,
    radius: number,
    speed: number,
    damage: number,
    expiresAt: number,
  ) {
    const projectile = this.getHazardCircle();
    if (!projectile) {
      return;
    }
    projectile.setPosition(x, y);
    projectile.setRadius(radius);
    projectile.setFillStyle(color, 0.88);
    projectile.setStrokeStyle(1, 0xffffff, 0.72);
    projectile.setActive(true).setVisible(true).setAlpha(1);
    projectile.setDepth(30);
    projectile.setData('expiresAt', expiresAt);
    projectile.setData('damage', damage);
    projectile.setData('damageSource', 'boss-projectile');
    projectile.setData('hitCooldownMs', 1000);
    projectile.setData('nextHitAt', 0);
    const body = projectile.body as Phaser.Physics.Arcade.Body;
    body.enable = true;
    body.setAllowGravity(false);
    body.setCircle(radius);
    body.setOffset(-radius, -radius);
    this.scene.physics.moveTo(projectile, targetX, targetY, speed);
  }

  private getHazardCircle() {
    let hazard = this.hazards.getFirstDead(false) as Phaser.GameObjects.Arc | null;
    if (!hazard && this.hazards.getLength() >= 48) {
      hazard = this.hazards.getChildren().find((child) => child.active) as Phaser.GameObjects.Arc | undefined ?? null;
      if (hazard) {
        this.deactivateHazard(hazard);
      }
    }
    if (!hazard) {
      hazard = this.scene.add.circle(0, 0, 10, 0xffffff, 0.4);
      this.scene.physics.add.existing(hazard);
      this.hazards.add(hazard);
    }
    return hazard;
  }

  private getShockwave() {
    let shockwave = this.shockwaves.getFirstDead(false) as Phaser.GameObjects.Rectangle | null;
    if (!shockwave) {
      if (this.shockwaves.getLength() >= 8) {
        return null;
      }
      shockwave = this.scene.add.rectangle(0, 0, 220, 40, 0xff3f35, 0.68);
      this.scene.physics.add.existing(shockwave);
      this.shockwaves.add(shockwave);
    }
    return shockwave;
  }

  private deactivateShockwave(shockwave: Phaser.GameObjects.Rectangle) {
    shockwave.setActive(false).setVisible(false).setAlpha(1);
    const body = shockwave.body as Phaser.Physics.Arcade.Body;
    body.enable = false;
    body.setVelocity(0, 0);
  }

  private deactivateHazard(hazard: Phaser.GameObjects.Shape) {
    this.scene.tweens.killTweensOf(hazard);
    hazard.setActive(false).setVisible(false).setAlpha(1);
    const body = hazard.body as Phaser.Physics.Arcade.Body | undefined;
    if (body) {
      body.enable = false;
      body.setVelocity(0, 0);
    }
  }

  private showPulse(x: number, y: number, radius: number, color: number, durationMs: number) {
    const pulse = this.scene.add.circle(x, y, radius, color, 0.18);
    pulse.setStrokeStyle(3, color, 0.78);
    pulse.setDepth(29);
    pulse.setScale(0.18);
    this.scene.tweens.add({
      targets: pulse,
      scale: 1,
      alpha: 0,
      duration: durationMs,
      onComplete: () => pulse.destroy(),
    });
  }

  private showShieldHit(boss: BossToken) {
    const shield = this.scene.add.circle(boss.x, boss.y, boss.radius + 18, 0xa8ff8f, 0.13);
    shield.setStrokeStyle(3, 0xd8ffd9, 0.82);
    shield.setDepth(29);
    this.scene.tweens.add({
      targets: shield,
      scale: 1.25,
      alpha: 0,
      duration: 180,
      onComplete: () => shield.destroy(),
    });
  }

  private showIronShield(boss: BossToken, angle: number) {
    const left = angle - (boss.getBossPhase() === 2 ? (Math.PI * 2) / 3 : Math.PI / 3);
    const right = angle + (boss.getBossPhase() === 2 ? (Math.PI * 2) / 3 : Math.PI / 3);
    const shield = this.scene.add.graphics();
    shield.setDepth(28);
    shield.fillStyle(0xd8edf7, 0.13);
    shield.lineStyle(3, 0xd8edf7, 0.72);
    shield.beginPath();
    shield.moveTo(boss.x, boss.y);
    shield.lineTo(boss.x + Math.cos(left) * 54, boss.y + Math.sin(left) * 54);
    shield.lineTo(boss.x + Math.cos(angle) * 68, boss.y + Math.sin(angle) * 68);
    shield.lineTo(boss.x + Math.cos(right) * 54, boss.y + Math.sin(right) * 54);
    shield.closePath();
    shield.fillPath();
    shield.strokePath();
    this.scene.tweens.add({
      targets: shield,
      alpha: 0,
      duration: 120,
      onComplete: () => shield.destroy(),
    });
  }

  private rebuildMirrorDecoys(boss: BossToken, count: number) {
    for (const decoy of boss.mirrorDecoys) {
      decoy.destroy();
    }
    boss.mirrorDecoys = [];
    for (let index = 0; index < count; index += 1) {
      const angle = (Math.PI * 2 * index) / count + this.scene.time.now * 0.001;
      const decoy = this.scene.add.circle(0, 0, boss.radius, boss.getBossPhase() === 2 ? 0x8cf2ff : 0x5ad0d4, 0.42);
      decoy.setStrokeStyle(1, 0xdcfbff, 0.22);
      decoy.setDepth(13);
      decoy.setData('offsetAngle', angle);
      decoy.setData('offsetRadius', 72 + index * 8);
      boss.mirrorDecoys.push(decoy);
      this.scene.tweens.add({
        targets: decoy,
        alpha: 0.12,
        duration: 150,
        yoyo: true,
        repeat: 1,
      });
    }
    boss.setStrokeStyle(2, 0xffffff, 0.18);
    this.showModeText(boss.x, boss.y - 42, '重排', '#dcfbff');
  }

  private updateMirrorDecoys(boss: BossToken, timeMs: number) {
    if (boss.mirrorDecoys.length === 0) {
      this.rebuildMirrorDecoys(boss, boss.getBossPhase() === 2 ? 4 : 2);
    }
    for (const [index, decoy] of boss.mirrorDecoys.entries()) {
      const angle = ((decoy.getData('offsetAngle') as number | undefined) ?? 0) + timeMs * (boss.getBossPhase() === 2 ? 0.0012 : 0.0008) * (index % 2 === 0 ? 1 : -1);
      const radius = (decoy.getData('offsetRadius') as number | undefined) ?? 72;
      decoy.setPosition(
        Phaser.Math.Clamp(boss.x + Math.cos(angle) * radius, 24, GAME_WIDTH - 24),
        Phaser.Math.Clamp(boss.y + Math.sin(angle) * radius, 104, GAME_HEIGHT - 24),
      );
      decoy.setVisible(boss.active);
      decoy.setActive(boss.active);
    }
  }

  private showModeText(x: number, y: number, label: string, color: string) {
    const text = this.scene.add.text(x, y, label, ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      color,
      fontStyle: 'bold',
    })).setOrigin(0.5);
    text.setDepth(80);
    this.scene.tweens.add({
      targets: text,
      alpha: 0,
      y: y - 14,
      duration: 520,
      onComplete: () => text.destroy(),
    });
  }

  private showBossIntro(label: string, skillSummary: string) {
    const text = this.scene.add.text(GAME_WIDTH / 2, 134, label, ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '24px',
      color: '#ffd6c8',
      fontStyle: 'bold',
    })).setOrigin(0.5);
    const skill = this.scene.add.text(GAME_WIDTH / 2, 164, skillSummary, ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      color: '#f5b88f',
      fontStyle: 'bold',
    })).setOrigin(0.5);
    text.setDepth(80);
    skill.setDepth(80);
    this.scene.tweens.add({
      targets: [text, skill],
      alpha: 0,
      y: '-=24',
      duration: 1200,
      onComplete: () => {
        text.destroy();
        skill.destroy();
      },
    });
  }

  private showBossAimLine(boss: BossToken) {
    const line = this.scene.add.line(0, 0, boss.x, boss.y, boss.shockwaveTargetX, boss.shockwaveTargetY, 0xff3f35, 0.5);
    line.setOrigin(0, 0);
    line.setLineWidth(6, 2);
    line.setDepth(27);
    this.scene.tweens.add({
      targets: line,
      alpha: 0,
      duration: 500,
      onComplete: () => line.destroy(),
    });
  }

  private showSummonWarning(x: number, y: number) {
    const ring = this.scene.add.circle(x, y, 72, 0xff594a, 0.18);
    ring.setStrokeStyle(3, 0xffc1a1, 0.86);
    ring.setDepth(27);
    ring.setScale(0.4);
    this.scene.tweens.add({
      targets: ring,
      scale: 1,
      alpha: 0,
      duration: 650,
      onComplete: () => ring.destroy(),
    });
  }
}
